import { Download } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { ExportJob } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { JobStatusBadge } from './job-status-badge';

export function useExportColumns(onDownload: (job: ExportJob) => void, downloadingId?: string) {
  const { t } = useTranslation('importsExports');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<ExportJob>>>(
    () => [
      {
        id: 'kind',
        header: t('columns.kind'),
        cell: ({ row }) => <span className="font-medium">{t(`kinds.${row.original.kind}`)}</span>,
        meta: { label: t('columns.kind'), filterKey: 'kind', lockVisibility: true },
        size: 150,
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => (
          <JobStatusBadge status={row.original.status} error={row.original.error} />
        ),
        meta: { label: t('columns.status'), filterKey: 'status' },
        size: 110,
      },
      {
        id: 'rows',
        header: t('columns.rows'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.rowCount === null ? '—' : format.number(row.original.rowCount)}
          </span>
        ),
        meta: { align: 'end' },
        size: 80,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('columns.created'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.createdAt)}>
            {format.relative(row.original.createdAt)}
          </span>
        ),
        meta: { sortField: 'createdAt', filterKey: 'createdAt' },
        size: 130,
      },
      {
        id: 'expiresAt',
        header: t('columns.expires'),
        cell: ({ row }) => {
          const { expiresAt, status } = row.original;
          if (!expiresAt || status !== 'completed') {
            return <span className="text-muted-foreground">—</span>;
          }
          const expired = new Date(expiresAt).getTime() <= Date.now();
          return (
            <span className="text-muted-foreground" title={format.utc(expiresAt)}>
              {expired ? t('expired') : format.relative(expiresAt)}
            </span>
          );
        },
        size: 130,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('columns.actions')}</span>,
        meta: { label: t('columns.actions') },
        cell: ({ row }) =>
          row.original.ready ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={downloadingId === row.original.id}
              onClick={() => onDownload(row.original)}
              aria-label={t('download.labelFor', { kind: t(`kinds.${row.original.kind}`) })}
            >
              <Download aria-hidden />
              {t('download.label')}
            </Button>
          ) : null,
        size: 120,
      },
    ],
    [downloadingId, format, onDownload, t],
  );
}
